import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import type Owner from '@ember/owner';
import Modal from 'tio-ui/components/modal';
import Button from 'tio-ui/components/button';
import { eq } from 'tio-ui/utilities';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type {
  DemoClientId,
  DemoProductId,
  DemoSessionRoleId,
  DemoTaPaymentModelId,
  DemoUserScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

interface DemoDataShortcutSignature {
  Args: Record<string, never>;
}

export default class DemoDataShortcut extends Component<DemoDataShortcutSignature> {
  @service declare demoMode: DemoModeService;

  @tracked isOpen = false;
  @tracked selectedClientId: DemoClientId;
  @tracked selectedProductId: DemoProductId;
  @tracked selectedRoleId: DemoSessionRoleId;
  @tracked selectedUserScenarioId: DemoUserScenarioId;
  @tracked selectedTaPaymentModelId: DemoTaPaymentModelId;

  constructor(owner: Owner, args: DemoDataShortcutSignature['Args']) {
    super(owner, args);

    const selection = this.demoMode.currentSelection;
    this.selectedClientId = selection.clientId;
    this.selectedProductId = selection.productId;
    this.selectedRoleId = selection.roleId;
    this.selectedUserScenarioId = selection.userScenarioId;
    this.selectedTaPaymentModelId = selection.taPaymentModelId;
  }

  /**
   * Installs the global Ctrl+Shift+~ listener for the lifetime of the host
   * element. Lives in a modifier (not the constructor) per repo convention and
   * is only ever rendered when demo mode is available, so the listener never
   * attaches in a session that cannot use it.
   */
  installShortcut = modifier(() => {
    const handler = (event: KeyboardEvent) => {
      if (!this.isDemoShortcut(event) || this.isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.openModal();
    };

    document.addEventListener('keydown', handler);

    return () => document.removeEventListener('keydown', handler);
  });

  get clients() {
    return this.demoMode.clients;
  }

  get products() {
    return this.demoMode.products;
  }

  get roles() {
    return this.demoMode.roles;
  }

  get userScenarios() {
    return this.demoMode.userScenarios;
  }

  get taPaymentModels() {
    return this.demoMode.taPaymentModels;
  }

  get selectedClientName() {
    return this.demoMode.clientName(this.selectedClientId);
  }

  get selectedProductName() {
    return this.demoMode.productName(this.selectedProductId);
  }

  get selectedRoleName() {
    return this.demoMode.roleName(this.selectedRoleId);
  }

  get selectedUserScenarioName() {
    return this.demoMode.userScenarioName(this.selectedUserScenarioId);
  }

  get selectedTaPaymentModelName() {
    return this.demoMode.taPaymentModelName(this.selectedTaPaymentModelId);
  }

  // Internal-only derived state (used by isEmployeeExperience, not the template).
  private get selectedRole() {
    return this.roles.find((role) => role.id === this.selectedRoleId);
  }

  /**
   * Whether the selected role simulates the employee/participant experience.
   * The "user scenario" cards are employee-journey personas (First Login,
   * New Employee, Power User, Benefits Maxed, …), so they only make sense for
   * the employee. The Supervisor (approver) and Admin (partner-admin) views have
   * no employee benefits journey to pick — forcing a scenario choice there is
   * incoherent, so the section is hidden for those roles.
   */
  get isEmployeeExperience() {
    return this.selectedRole?.relationshipType === 'PARTICIPANT';
  }

  private isDemoShortcut(event: KeyboardEvent) {
    return event.ctrlKey && event.shiftKey && (event.key === '~' || event.code === 'Backquote');
  }

  private isEditableTarget(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target.isContentEditable ||
      !!target.closest('[contenteditable=""], [contenteditable="true"]')
    );
  }

  openModal = () => {
    const selection = this.demoMode.currentSelection;
    this.selectedClientId = selection.clientId;
    this.selectedProductId = selection.productId;
    this.selectedRoleId = selection.roleId;
    this.selectedUserScenarioId = selection.userScenarioId;
    this.selectedTaPaymentModelId = selection.taPaymentModelId;
    this.isOpen = true;
  };

  closeModal = () => {
    this.isOpen = false;
  };

  selectClient = (clientId: DemoClientId) => {
    this.selectedClientId = clientId;
  };

  selectProduct = (productId: DemoProductId) => {
    this.selectedProductId = productId;
  };

  selectRole = (roleId: DemoSessionRoleId) => {
    this.selectedRoleId = roleId;
  };

  selectUserScenario = (userScenarioId: DemoUserScenarioId) => {
    this.selectedUserScenarioId = userScenarioId;
  };

  selectTaPaymentModel = (taPaymentModelId: DemoTaPaymentModelId) => {
    this.selectedTaPaymentModelId = taPaymentModelId;
  };

  apply = () => {
    this.demoMode.apply({
      clientId: this.selectedClientId,
      productId: this.selectedProductId,
      roleId: this.selectedRoleId,
      userScenarioId: this.selectedUserScenarioId,
      taPaymentModelId: this.selectedTaPaymentModelId,
    });
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{#if this.demoMode.isAvailable}}
      <div hidden {{this.installShortcut}}></div>

      <Modal
        @isOpen={{this.isOpen}}
        @onClose={{this.closeModal}}
        @size="2xl"
        @ariaLabel="Demo data options"
        data-test-demo-data-modal
      >
        <:header>
          <div>
            <h2 class="tio-text-lg-semibold tio-fg-primary">Demo data</h2>
            <p class="mt-1 text-sm tio-fg-muted">
              Choose the client, product, and role to apply to this demo session.
            </p>
          </div>
        </:header>

        <:body>
          <div class="space-y-8">
            <section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Client</h3>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {{#each this.clients as |client|}}
                  <button
                    type="button"
                    class="rounded-xl border p-4 text-left transition
                      {{if
                        (eq client.id this.selectedClientId)
                        'border-violet-500 bg-violet-50 ring-2 ring-violet-100'
                        'border-gray-200 hover:border-violet-300 hover:bg-violet-50/50'
                      }}"
                    aria-pressed={{if (eq client.id this.selectedClientId) "true" "false"}}
                    data-test-demo-client={{client.id}}
                    {{on "click" (fn this.selectClient client.id)}}
                  >
                    <div class="font-semibold tio-fg-primary">{{client.name}}</div>
                    <div class="mt-1 text-sm tio-fg-muted">{{client.industry}}</div>
                    <div class="mt-2 text-xs tio-fg-disabled">
                      {{client.employeeCount}}
                      employees &middot;
                      {{client.taUtilization}}% TA utilization
                    </div>
                  </button>
                {{/each}}
              </div>
            </section>

            <section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Product</h3>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {{#each this.products as |product|}}
                  <button
                    type="button"
                    class="rounded-xl border p-4 text-left transition
                      {{if
                        (eq product.id this.selectedProductId)
                        'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100'
                        'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                      }}"
                    aria-pressed={{if (eq product.id this.selectedProductId) "true" "false"}}
                    data-test-demo-product={{product.id}}
                    {{on "click" (fn this.selectProduct product.id)}}
                  >
                    <div class="font-semibold tio-fg-primary">{{product.name}}</div>
                    <div class="mt-1 text-sm tio-fg-muted">{{product.description}}</div>
                  </button>
                {{/each}}
              </div>
            </section>

            <section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Role</h3>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {{#each this.roles as |role|}}
                  <button
                    type="button"
                    class="rounded-xl border p-4 text-left transition
                      {{if
                        (eq role.id this.selectedRoleId)
                        'border-sky-500 bg-sky-50 ring-2 ring-sky-100'
                        'border-gray-200 hover:border-sky-300 hover:bg-sky-50/50'
                      }}"
                    aria-pressed={{if (eq role.id this.selectedRoleId) "true" "false"}}
                    data-test-demo-role={{role.id}}
                    {{on "click" (fn this.selectRole role.id)}}
                  >
                    <div class="font-semibold tio-fg-primary">{{role.name}}</div>
                    <div class="mt-1 text-sm tio-fg-muted">{{role.description}}</div>
                  </button>
                {{/each}}
              </div>
            </section>

            {{#if this.isEmployeeExperience}}
              <section>
                <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                  User scenario
                </h3>
                <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {{#each this.userScenarios as |scenario|}}
                    <button
                      type="button"
                      class="rounded-xl border p-4 text-left transition
                        {{if
                          (eq scenario.id this.selectedUserScenarioId)
                          'border-amber-500 bg-amber-50 ring-2 ring-amber-100'
                          'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                        }}"
                      aria-pressed={{if
                        (eq scenario.id this.selectedUserScenarioId)
                        "true"
                        "false"
                      }}
                      data-test-demo-user-scenario={{scenario.id}}
                      {{on "click" (fn this.selectUserScenario scenario.id)}}
                    >
                      <div class="font-semibold tio-fg-primary">{{scenario.name}}</div>
                      <div class="mt-1 text-sm tio-fg-muted">{{scenario.description}}</div>
                    </button>
                  {{/each}}
                </div>
              </section>
            {{/if}}

            <section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                TA payment model
              </h3>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {{#each this.taPaymentModels as |model|}}
                  <button
                    type="button"
                    class="rounded-xl border p-4 text-left transition
                      {{if
                        (eq model.id this.selectedTaPaymentModelId)
                        'border-rose-500 bg-rose-50 ring-2 ring-rose-100'
                        'border-gray-200 hover:border-rose-300 hover:bg-rose-50/50'
                      }}"
                    aria-pressed={{if (eq model.id this.selectedTaPaymentModelId) "true" "false"}}
                    data-test-demo-ta-payment-model={{model.id}}
                    {{on "click" (fn this.selectTaPaymentModel model.id)}}
                  >
                    <div class="font-semibold tio-fg-primary">{{model.name}}</div>
                    <div class="mt-1 text-sm tio-fg-muted">{{model.description}}</div>
                  </button>
                {{/each}}
              </div>
            </section>
          </div>
        </:body>

        <:footer>
          <div class="tio-col sm:flex-row sm:items-center sm:justify-between">
            <p class="text-sm tio-fg-muted">
              Applying:
              <span class="font-medium tio-fg-secondary">{{this.selectedClientName}}</span>
              /
              <span class="font-medium tio-fg-secondary">{{this.selectedProductName}}</span>
              /
              <span class="font-medium tio-fg-secondary">{{this.selectedRoleName}}</span>
              {{#if this.isEmployeeExperience}}
                /
                <span class="font-medium tio-fg-secondary">{{this.selectedUserScenarioName}}</span>
              {{/if}}
              /
              <span class="font-medium tio-fg-secondary">{{this.selectedTaPaymentModelName}}</span>
            </p>
            <div class="tio-flex-end gap-3">
              <Button @appearance="outlined" @intent="default" {{on "click" this.closeModal}}>
                Cancel
              </Button>
              <Button @intent="primary" data-test-demo-apply {{on "click" this.apply}}>
                Apply demo data
              </Button>
            </div>
          </div>
        </:footer>
      </Modal>
    {{/if}}
  </template>
}
